import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { Exclude, Expose } from "class-transformer";
import { IsEnum, IsNumber, IsOptional } from "class-validator";
import { Subject } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export enum WildcardZoneType {
  ZONE_0 = "ZONE_0",
  ZONE_1 = "ZONE_1",
  ZONE_2 = "ZONE_2",
}

export class WildcardZone extends FloorplanItem {
  @IsEnum(WildcardZoneType)
  public type: WildcardZoneType;

  @Exclude()
  public sizeChanged: Subject<{ width: number; height: number }>;

  @Exclude()
  public referenceSizeChanged: Subject<{ referenceWidth: number | undefined; referenceHeight: number | undefined }>;

  @IsNumber()
  @Expose({ name: "width" })
  private _width: number;

  @IsNumber()
  @Expose({ name: "height" })
  private _height: number;

  @IsNumber()
  @IsOptional()
  @Expose({ name: "referenceWidth" })
  private _referenceWidth?: number;

  @IsNumber()
  @IsOptional()
  @Expose({ name: "_referenceHeight" })
  private _referenceHeight?: number;

  @IsNumber()
  @Expose({ name: "rotation" })
  private _rotation: number;

  constructor(
    floorplan: Floorplan,
    id: string,
    type: WildcardZoneType,
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number,
  ) {
    super(floorplan, id, x, y);
    this._width = width;
    this._height = height;
    this._rotation = rotation;
    this.type = type;
    this.sizeChanged = new Subject();
    this.referenceSizeChanged = new Subject();
    this.updateReferenceSize(this._width, this._height);
  }

  get width(): number {
    return this._width;
  }

  get height(): number {
    return this._height;
  }

  get rotation(): number {
    return this._rotation;
  }

  get referenceWidth(): number | undefined {
    return this._referenceWidth;
  }

  get referenceHeight(): number | undefined {
    return this._referenceHeight;
  }

  updateReferenceSize(width: number, height: number) {
    if (this.isReferenceMeasureSet() && width && height) {
      this._referenceWidth = Math.round((width / this._floorplan.getReferenceScale()) * 100) / 100;
      this._referenceHeight = Math.round((height / this._floorplan.getReferenceScale()) * 100) / 100;
    } else {
      this._referenceWidth = undefined;
      this._referenceHeight = undefined;
    }
  }

  updateSize(referenceWidth: number, referenceHeight: number) {
    this._width = referenceWidth * this._floorplan.getReferenceScale();
    this._height = referenceHeight * this._floorplan.getReferenceScale();
    this.updateForm(this._width, this._height, this._rotation, this.x, this.y);
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_SIZE_CHANGED_BY_INPUT, this);
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.wildcardZonesItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const height = this.height;
    const rotation = this.rotation;
    const type = this.type;
    const width = this.width;

    return (x: number, y: number) => WildcardZone.create(this._floorplan, type, x, y, width, height, rotation);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.wildcardZones;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as WildcardZone,
        this._floorplan.wildcardZones,
        FloorplanEventType.WILDCARD_ZONE_ADDED,
        () => this._floorplan.project.setWildcardZonePositionIds(),
        afterDuplicateAdded as ((duplicate: WildcardZone) => void) | undefined,
      );
  }

  override delete() {
    this.sizeChanged.unsubscribe();
    this._floorplan.deleteWildcardZone(this);
  }

  updateForm(width: number, height: number, rotation: number, x: number, y: number) {
    this.sizeChanged.next({ width, height });
    this.updateReferenceSize(width, height);
    this._width = width;
    this._height = height;
    this._rotation = rotation;
    this.updatePosition(x, y);
  }

  isReferenceMeasureSet() {
    if (this._floorplan) return !!this._floorplan.referenceMeasure;
    return false;
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshWildcardZonesLock();
  }

  static create(
    floorplan: Floorplan,
    type: WildcardZoneType,
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number,
  ) {
    return new WildcardZone(floorplan, uuidv4(), type, x, y, width, height, rotation);
  }
}
